import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Image from '~/components/base/Image';
import BaseLink from '~/components/base/BaseLink';
import IconList from '~/components/IconList';

import { transition } from '~/styles/transition';

import {
    CarouselGrid,
    TextSliderWrapper,
    TitleWrapper,
    Title,
    ImageSliderProvider
} from '~/components/blocks/acf/FeaturesCarousel';
import TextSlider from '~/components/features-carousel/TextSlider';
import ImageSlider from '~/components/features-carousel/ImageSlider';

import { h2 } from '~/styles/typography';
import { calcVw } from '~/styles/helpers';

import { hexToRgba, formatText } from '~/utilities/helpers';

import phoneScreen from '~/images/phone.png';
import radialPlusApp from '~/images/singleIllustrations/radialPlusApp.png';
import appShapes from '~/images/singleIllustrations/appShapes.png';

const AppButtons = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 15px;
    margin-top: 60px;
    z-index: 2;
    max-width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        flex-direction: row;
        position: absolute;
        pointer-events: all;
        margin-top: 0;
        padding-top: 120px;
        bottom: calc(50% - 120px);
        transform: ${({ singleSlide }) =>
            singleSlide ? `translate(-50%, 100%)` : `translateY(100%)`};
        gap: 30px;
        width: calc(100% - 75px);
        right: ${({ singleSlide }) => (singleSlide ? `auto` : `0`)};
        left: ${({ singleSlide }) => (singleSlide ? `50%` : `auto`)};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        max-width: 530px;
        justify-self: flex-end;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeDesktop}) {
        max-width: 650px;
    }
`;

const AppImage = styled(Image)`
    max-width: 156px;
    ${transition({}, 'box-shadow', 'outline')};
`;

const AppLink = styled(BaseLink)`
    &:hover,
    &:focus {
        ${AppImage} {
            box-shadow: 0 2px 20px 0
                ${({ theme }) => hexToRgba(theme.colors.primary.black, 0.75)};
            outline: 2px solid ${({ theme }) => theme.colors.text.lightBlue};
        }
    }
`;

const MobileContent = styled.div`
    padding: 30px 20px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 15px;
    justify-content: space-between;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: ${calcVw(-50)};
    margin-right: ${calcVw(-50)};
    width: ${calcVw(100)};
    overflow: hidden;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: none;
    }
`;

const MobileTitle = styled.h2`
    ${h2};
    color: ${({ theme }) => theme.colors.primary.white};
    margin-bottom: 30px;
    text-align: center;
    flex: 1 1 100%;
`;

const MobileTextContent = styled.div`
    flex: 0 1 calc(100% - 245px);
    max-width: calc(100% - 245px);
`;

const MobileImageWrapper = styled.div`
    flex: 0 0 230px;
    height: 100%;
`;

const MobileImage = styled(Image)`
    height: 100%;
`;

const PhoneImageWrapper = styled.div`
    position: relative;
    transform: translateX(30px);

    ${MobileImage} {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 330px;
        width: 155px;
        border-radius: 21px;
    }
`;

const PhoneScreen = styled(Image)`
    width: 170px;
    height: 345px;
    transform: translateX(30px);
`;

const PhoneShapesTop = styled(Image)`
    position: absolute;
    width: 150px;
    top: -20%;
    right: -25px;
    z-index: -1;
`;

const PhoneShapesBottom = styled(Image)`
    position: absolute;
    width: 95px;
    bottom: 50px;
    z-index: -1;
    left: 0;
`;
const FeaturesCarouselWithPhone = ({
    title,
    slides,
    mobileContent,
    appLinks,
    state,
    dispatch
}) => {
    return (
        <>
            <CarouselGrid hideOnMobile={true} singleSlide={slides.length < 2}>
                <TextSliderWrapper>
                    <TitleWrapper>
                        <Title noButtons={slides.length < 2}>
                            {formatText(title)}
                        </Title>
                        <AppButtons singleSlide={slides.length < 2}>
                            {appLinks?.length
                                ? appLinks.map(({ linkUrl, badge }) => (
                                      <AppLink
                                          key={linkUrl}
                                          to={linkUrl}
                                          external
                                      >
                                          <AppImage {...badge} />
                                      </AppLink>
                                  ))
                                : null}
                        </AppButtons>
                    </TitleWrapper>
                    <TextSlider
                        {...{ slides }}
                        featuresCarouselState={state}
                        featuresCarouselDispatch={dispatch}
                    />
                </TextSliderWrapper>
                <ImageSliderProvider
                    totalSlides={slides.length}
                    naturalSlideHeight={800}
                    naturalSlideWidth={500}
                    visibleSlides={1}
                    isIntrinsicHeight={true}
                >
                    <ImageSlider
                        {...{ slides }}
                        usePhoneScreen={true}
                        featuresCarouselState={state}
                        featuresCarouselDispatch={dispatch}
                    />
                </ImageSliderProvider>
            </CarouselGrid>

            <MobileContent>
                <MobileTitle>{formatText(mobileContent.title)}</MobileTitle>
                <MobileTextContent>
                    <IconList features={mobileContent.features} />
                    <AppButtons singleSlide={slides.length < 2}>
                        {appLinks?.length
                            ? appLinks.map(({ linkUrl, badge }) => (
                                  <AppLink key={linkUrl} to={linkUrl} external>
                                      <AppImage {...badge} />
                                  </AppLink>
                              ))
                            : null}
                    </AppButtons>
                </MobileTextContent>
                <MobileImageWrapper>
                    <PhoneImageWrapper>
                        <PhoneShapesTop sourceUrl={radialPlusApp} />
                        <PhoneShapesBottom sourceUrl={appShapes} />
                        <PhoneScreen sourceUrl={phoneScreen} />
                        <MobileImage {...slides[0].image} />
                    </PhoneImageWrapper>
                </MobileImageWrapper>
            </MobileContent>
        </>
    );
};

FeaturesCarouselWithPhone.propTypes = {
    title: PropTypes.string.isRequired,
    slides: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            description: PropTypes.string,
            image: PropTypes.shape({
                id: PropTypes.string.isRequired,
                altText: PropTypes.string,
                sourceUrl: PropTypes.string.isRequired,
                localFile: PropTypes.shape({
                    childImageSharp: PropTypes.shape({
                        gatsbyImageData: PropTypes.object.isRequired
                    })
                })
            })
        })
    ),
    mobileContent: PropTypes.shape({
        title: PropTypes.string.isRequired,
        features: PropTypes.arrayOf(
            PropTypes.shape({
                icon: PropTypes.string.isRequired,
                feature: PropTypes.string.isRequired
            })
        )
    }).isRequired,
    appLinks: PropTypes.arrayOf(
        PropTypes.shape({
            linkUrl: PropTypes.string.isRequired,
            badge: PropTypes.shape({
                id: PropTypes.string.isRequired,
                altText: PropTypes.string,
                sourceUrl: PropTypes.string.isRequired,
                localFile: PropTypes.shape({
                    childImageSharp: PropTypes.shape({
                        gatsbyImageData: PropTypes.object.isRequired
                    })
                })
            })
        })
    )
};

export default FeaturesCarouselWithPhone;
